import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useShopStore } from "@/store/shopStore";
import { useUserStore } from "@/store/userStore";
import { PriorityRole, RoleEnum } from "@/types";
import { ROLE_LABELS } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateUserSchema } from "../schemas";
import { UpdateUserFormData } from "../types";
import { useAuth } from "@/contexts/AuthContext";
import { useGroupStore } from "@/store/groupStore";

const ROLE_OPTIONS = [
  { label: ROLE_LABELS.admin, value: RoleEnum.COMPANY_ADMIN },
  { label: ROLE_LABELS.head, value: RoleEnum.HEAD_OFFICE_USER },
  { label: ROLE_LABELS.store, value: RoleEnum.STORE_MANAGER },
  { label: ROLE_LABELS.user, value: RoleEnum.USER },
];

const useUpdateInforForm = () => {
  const { currentUserInfor, updateUserInfor, resendInfor } = useUserStore();
  const { shopsInCompany, fetchShopsInCompany } = useShopStore();
  const { groupsInCompany, fetchGroupsInCompany } = useGroupStore();
  const { user, role, updateUser, updateRole } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const companyId = useMemo(
    () => currentUserInfor?.company?.id,
    [currentUserInfor]
  );

  const isMyProfile = useMemo(() => {
    return currentUserInfor?.email?.trim() === user?.email?.trim();
  }, [currentUserInfor?.email, user?.email]);

  const hasUpdatePermission = useMemo(() => {
    return (
      PriorityRole[role || RoleEnum.OTHER] >
        PriorityRole[currentUserInfor?.role.name || RoleEnum.OTHER] ||
      role === RoleEnum.COMPANY_ADMIN
    );
  }, [role, currentUserInfor?.role.name]);

  const {
    control,
    reset,
    register,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      full_name: "",
      role: RoleEnum.USER,
      phone: "",
      email: "",
      company_name: "",
      position: "",
      department: "",
      address: "",
      store_id: undefined,
      group_ids: [],
    },
    resolver: zodResolver(UpdateUserSchema),
  });

  const isDisabledStoreSelect = useMemo(
    () =>
      watch("role") === RoleEnum.COMPANY_ADMIN ||
      watch("role") === RoleEnum.HEAD_OFFICE_USER,
    [watch("role")]
  );

  const onSubmit = async (data: UpdateUserFormData) => {
    if (isLoading) return;

    setIsLoading(true);

    if (isDisabledStoreSelect) {
      data.store_id = undefined;
    }

    const submitData: UpdateUserFormData = {
      ...data,
      address: data.address || undefined,
      department: data.department || undefined,
      phone: data.phone || undefined,
      position: data.position || undefined,
      store_id: data.store_id || undefined,
      group_ids: data.group_ids || [],
    };

    await updateUserInfor(Number(currentUserInfor?.id), submitData).finally(
      () => setIsLoading(false)
    );
    if (isMyProfile) {
      // Trigger to update user info in context
      // Just update with role at the moment
      // TODO: Update other fields if needed in the future
      updateUser({ ...user, role: data.role } as any);
      updateRole(data.role);
    }
  };

  const handleResendInfor = async () => {
    setIsLoading(true);
    await resendInfor(Number(currentUserInfor?.id)).finally(() =>
      setIsLoading(false)
    );
  };

  useEffect(() => {
    if (!currentUserInfor) return;

    reset({
      full_name: currentUserInfor.full_name,
      role: currentUserInfor.role.name,
      phone: currentUserInfor.phone || undefined,
      email: currentUserInfor.email,
      company_name: currentUserInfor.company.name,
      position: currentUserInfor.position || undefined,
      department: currentUserInfor.department || undefined,
      address: currentUserInfor.address || undefined,
      store_id: currentUserInfor.store.id || undefined,
      group_ids: currentUserInfor.groups?.map((group) => group.group_id) || [],
    });
  }, [currentUserInfor]);

  useEffect(() => {
    // Load initial shops
    // Only load stores/groups when the current user's role is not OTHER
    if (companyId && currentUserInfor?.role?.name !== RoleEnum.OTHER) {
      fetchShopsInCompany({
        companyId: Number(companyId),
      });
      // Only company admin can upgrade user's role to head office
      if (
        role === RoleEnum.COMPANY_ADMIN ||
        role === RoleEnum.HEAD_OFFICE_USER
      ) {
        fetchGroupsInCompany(role || RoleEnum.HEAD_OFFICE_USER, {
          companyId: Number(companyId),
        });
      }
    }
  }, [companyId]);

  return {
    control,
    register,
    roleOptionsFilter: ROLE_OPTIONS,
    errors,
    onSubmit,
    shopsInCompany,
    groupsInCompany: groupsInCompany.data,
    isDisabledStoreSelect,
    isMyProfile,
    hasUpdatePermission,
    watch,
    isLoading,
    handleResendInfor,
    handleSubmit,
  };
};

export { useUpdateInforForm };
